import React from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'reactstrap';

import * as JKYModel from '../custom-types';
import { PendingGoogleSignup, completeGoogleSignup, usernameLengthProblem } from '../utils/GoogleSignIn';

import './SignupView.scss';

interface GoogleUsernameStepProps {
  pending: PendingGoogleSignup;
  onSignedIn: (user: JKYModel.User, token: string) => void;
  onCancel: () => void;
}

/**
 * Someone new signed in with Google: they pick the name shown beside their songs, and agree
 * to the terms as everyone signing up does, before the account is made.
 */
const GoogleUsernameStep = ({ pending, onSignedIn, onCancel }: GoogleUsernameStepProps) => {
  const [username, setUsername] = React.useState(pending.suggestedUsername || '');
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const problem = usernameLengthProblem(username);
    if (problem) {
      setError(problem);
      return;
    }
    if (!acceptedTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { user, token } = await completeGoogleSignup(pending.pendingToken, username.trim(), acceptedTerms);
      onSignedIn(user, token);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="public-page-formwrapper">
      <h1 className="public-page-formaccess">Pick a Username</h1>
      <form onSubmit={handleSubmit} noValidate className="public-page-form">
        <p className="google-step-intro">
          {pending.email ? `Signing up as ${pending.email}. ` : ''}This name shows beside the songs you add.
        </p>
        {error && <Alert color="danger">{error}</Alert>}
        <input
          type="text"
          name="username"
          placeholder="Username"
          aria-label="Username"
          autoComplete="username"
          maxLength={20}
          value={username}
          onChange={e => setUsername(e.target.value.trim())}
        />
        <label className="signup-terms">
          <input
            type="checkbox"
            name="acceptedTerms"
            checked={acceptedTerms}
            onChange={e => setAcceptedTerms(e.target.checked)}
          />
          <span>
            I agree to the{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
            .
          </span>
        </label>
        <button className="button" disabled={loading}>
          {loading ? 'Creating…' : 'Create Account'}
        </button>
      </form>
      <div className="signin-link-wrapper">
        <button type="button" className="signin-link google-step-cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default GoogleUsernameStep;
